import { useMemo, useState } from 'react';
import { Box, Button, Divider, ListItem } from '@mui/material';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { ListHeader, ListRow, SidebarItem } from '/src/components';
import { useUserStore } from '/src/context';
import { shortDate } from '/src/lib/constants';
import { Lender } from '/src/lib/models';
import { currency } from '/src/utils';

export type AccountLedgerProps = {
  /**
   * Whether this component is visible
   */
  open: boolean;
  /**
   * Toggles component visibility
   * @returns void
   */
  toggleOpen: () => void;
  /**
   * Shows the Ledger modal
   * @param show Whether to show the modal
   * @returns void
   */
  setShowLedgerPopup: (show: boolean) => void;
  /**
   * Lender to display account ledger for
   */
  lender?: Lender;
};

export const AccountLedger: React.FC<AccountLedgerProps> = observer(
  ({ lender, open, toggleOpen, setShowLedgerPopup }) => {
    /** Context **/
    const userStore = useUserStore();

    /** State **/
    const [sortType, setSortType] = useState('sequenceNumber');
    const [sortReverse, setSortReverse] = useState(true);

    /** Computed **/
    const transactions = lender?.account.activity;

    const listData = useMemo(() => {
      if (transactions) {
        const list = transactions.toSorted((a, b) => (get(a, sortType) > get(b, sortType) ? 1 : -1));
        if (sortReverse) list.reverse();

        return list;
      } else return [];
    }, [transactions, sortType, sortReverse]);

    /** Methods **/

    const sortList = (type?: string) => {
      if (type) {
        if (sortType === type) setSortReverse(!sortReverse);
        else setSortType(type);
      }
    };

    /** Render **/
    return (
      <SidebarItem
        title="Account Ledger"
        open={open}
        toggleOpen={toggleOpen}
        toggleTooltip="Toggles the Account Ledger sidebar item"
        resolved={!!lender}
      >
        {userStore.isAdmin && (
          <>
            <ListItem>
              <Box display="flex" flex={1} />
              <Box display="flex">
                <Button aria-label="Post Transaction" className="md-mini thin" onClick={() => setShowLedgerPopup(true)}>
                  Post Transaction
                </Button>
              </Box>
            </ListItem>
            <Divider />
          </>
        )}

        {!listData.length && (
          <ListItem>
            <Box display="flex" flex={1} className="md-subhead-sm" justifyContent="center">
              You haven't posted any transactions yet!
            </Box>
          </ListItem>
        )}

        {!!listData.length && (
          <ListHeader
            items={[
              { propName: 'effectiveDate', label: 'Date' },
              { propName: 'amount', label: 'Credit/Debit', textAlign: 'right' },
              { propName: 'balance', label: 'Balance', textAlign: 'right' },
            ]}
            sortType={sortType}
            sortReverse={sortReverse}
            sortList={sortList}
          />
        )}

        <Divider />

        {!!listData.length &&
          listData?.map((txn, i) => (
            <ListRow
              key={txn._id}
              items={[
                { propName: 'effectiveDate', content: dayjs(txn.effectiveDate).format(shortDate), textAlign: 'right' },
                { propName: 'amount', content: currency.format(txn.amount), textAlign: 'right' },
                { propName: 'balance', content: currency.format(txn.balance), textAlign: 'right' },
              ]}
              rowIndex={i}
            />
          ))}
      </SidebarItem>
    );
  }
);
